/**
 * Универсальный шаблон публичных текстовых страниц: FAQ, Помощь,
 * Контакты, Соглашение, Конфиденциальность, Возврат кредитов, Правила
 * использования (TZ §12)
 *
 * Контент хранится прямо в коде (markdown-style array). Когда понадобится
 * редактировать без redeploy - сохранить тот же массив в public.settings
 * и читать через /api/public/static-page/{slug}. Пока MVP - захардкожено
 */
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/Card";
import { Logo } from "@/components/ui/Logo";

export interface StaticBlock {
  /** Заголовок раздела (h2). Если пустой - рендерим только параграфы. */
  heading?: string;
  /** Параграфы. Каждый - отдельный <p>. */
  paragraphs?: string[];
  /** Список (рендерится как <ul>). */
  list?: string[];
  /** Пара Q/A - для FAQ. */
  qa?: Array<{ q: string; a: string }>;
}

export interface StaticPageProps {
  title: string;
  subtitle?: string;
  /** Meta description для SEO. Если не передан, генерится из subtitle. */
  metaDescription?: string;
  blocks: StaticBlock[];
}

export function StaticPage({ title, subtitle, metaDescription, blocks }: StaticPageProps) {
  const desc = metaDescription || subtitle || `${title} - SpectrAI`;

  return (
    <div className="min-h-screen bg-bg text-ink">
      <Helmet>
        <title>{title} - SpectrAI</title>
        <meta name="description" content={desc} />
      </Helmet>

      <header className="sticky top-0 z-30 border-b border-line-soft/60 bg-bg/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <a href="/" className="inline-flex"><Logo size={26} /></a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-ink-mute">
            <a href="/" className="hover:text-ink">Главная</a>
            <a href="/faq" className="hover:text-ink">FAQ</a>
            <a href="/help" className="hover:text-ink">Помощь</a>
            <a href="/contacts" className="hover:text-ink">Контакты</a>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 lg:py-16 break-words">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-ink break-words">{title}</h1>
        {subtitle && <p className="mt-3 text-ink-mute">{subtitle}</p>}

        <div className="mt-8 space-y-8">
          {blocks.map((b, i) => (
            <section key={i} className="space-y-3">
              {b.heading && <h2 className="text-xl font-semibold text-ink">{b.heading}</h2>}
              {b.paragraphs?.map((p, j) => (
                <p key={j} className="text-sm text-ink-mute leading-relaxed whitespace-pre-wrap">{p}</p>
              ))}
              {b.list && (
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-ink-mute">
                  {b.list.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              )}
              {b.qa && (
                <div className="space-y-2">
                  {b.qa.map((qa, j) => (
                    <Card key={j} className="p-4">
                      <div className="text-sm font-semibold text-ink mb-1.5">{qa.q}</div>
                      <p className="text-sm text-ink-mute whitespace-pre-wrap">{qa.a}</p>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-line-soft bg-surface-2/40 mt-12">
        <div className="mx-auto max-w-3xl px-4 py-6 text-xs text-ink-soft flex items-center justify-between">
          <span>© {new Date().getFullYear()} SpectrAI</span>
          <a href="/" className="hover:text-ink">На главную</a>
        </div>
      </footer>
    </div>
  );
}
